import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { authApi, AuthApiError } from '../api/authApi';
import { useAuthStore } from '../store/useAuthStore';
import { useMeStore } from '../store/useMeStore';
import { useStrikesStore } from '../store/useStrikesStore';

export const ME_QUERY_KEY = ['auth', 'me'] as const;

export function useMe() {
  const setUser = useAuthStore((s) => s.setUser);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const hydrateMe = useMeStore((s) => s.hydrateFromMe);
  const hydrateStrikes = useStrikesStore((s) => s.hydrateFromMe);

  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: authApi.me,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof AuthApiError && error.status === 401) return false;
      return failureCount < 2;
    },
  });

  useEffect(() => {
    if (query.data) {
      setUser(
        {
          userId: query.data.user_id,
          nickname: query.data.nickname,
          email: query.data.email,
          authMethod: query.data.auth_method,
          tierId: query.data.tier_id,
          tierKey: query.data.tier_key,
          ageVerificationStatus: query.data.age_verification_status,
        },
        query.data.csrf_token,
      );
      hydrateMe(
        query.data.pip_default_on,
        query.data.safety_checkin_enabled,
        query.data.friend_count,
        query.data.pending_friend_request_count,
        query.data.presence_privacy,
      );
      hydrateStrikes({
        strikes_count: query.data.strikes_count,
        is_banned: query.data.is_banned,
        banned_until: query.data.banned_until,
      });
    } else if (query.isError) {
      clearAuth();
    }
  }, [query.data, query.isError, setUser, clearAuth, hydrateMe, hydrateStrikes]);

  return query;
}
